/**
 * Delivery of the Shop Stock Count closing report.
 *
 * Transport is the existing `send-email` edge function via `sendEdgeEmail`.
 * Nothing new is deployed for this.
 */

import { supabase } from './supabase'
import { sendEdgeEmail } from './b2bEmail'
import { htmlToPdfBase64 } from './pdfGenerator'
import { createContextLogger } from './logger'
import {
	buildShopStockCountMatrix,
	type ShopStockCountReportLine,
} from '../components/shop-stock-count/shop-stock-count-report'

const log = createContextLogger('ShopStockCountEmail')

export interface WorkerContactOption {
	id: string
	name: string
	email: string
	job_function: string | null
}

export async function isEmailConfigured(adminId: string): Promise<boolean> {
	if (!adminId) return false

	const { data, error } = await supabase
		.from('admin_brevo_credentials')
		.select('id')
		.eq('admin_id', adminId)
		.eq('is_active', true)
		.maybeSingle()

	if (error) {
		log.warn('Could not check email configuration', { adminId, error: error.message })
		return true
	}

	return Boolean(data)
}

export async function fetchWorkerContacts(adminId: string): Promise<WorkerContactOption[]> {
	if (!adminId) return []

	const { data, error } = await supabase
		.from('worker_contacts')
		.select('id, name, email, job_function')
		.eq('admin_id', adminId)
		.order('name')

	if (error) {
		log.warn('Could not load worker contacts', { adminId, error: error.message })
		return []
	}

	return (data as WorkerContactOption[]) ?? []
}

interface CountSummaryForEmail {
	countNumber: string
	locationName?: string | null
	countedByName?: string | null
	lines: ShopStockCountReportLine[]
}

function buildEmailBody(summary: CountSummaryForEmail, intro: string): string {
	const matrix = buildShopStockCountMatrix(summary.lines)

	return `
    <div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;color:#1f2937;">
      <div style="background:#0f766e;color:#fff;padding:22px 28px;border-radius:8px 8px 0 0;">
        <h1 style="margin:0;font-size:21px;">Shop Stock Count</h1>
        <p style="margin:6px 0 0;opacity:0.9;font-size:14px;">${summary.countNumber}</p>
      </div>
      <div style="padding:26px 28px;background:#fff;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;">
        <p style="margin:0 0 18px;">${intro}</p>

        <table style="width:100%;border-collapse:separate;border-spacing:0;margin-bottom:20px;">
          <tr>
            <td style="padding:0 4px;width:50%;">
              <div style="background:#f0fdfa;border:1px solid #99f6e4;border-radius:8px;padding:12px;text-align:center;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-weight:700;color:#0f766e;">Units</div>
                <div style="font-size:26px;font-weight:800;color:#0f766e;line-height:1.1;">${matrix.totalUnits}</div>
              </div>
            </td>
            <td style="padding:0 4px;width:50%;">
              <div style="background:#f0fdfa;border:1px solid #99f6e4;border-radius:8px;padding:12px;text-align:center;">
                <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-weight:700;color:#0f766e;">Products</div>
                <div style="font-size:26px;font-weight:800;color:#0f766e;line-height:1.1;">${matrix.totalSkus}</div>
              </div>
            </td>
          </tr>
        </table>

        <div style="background:#f9fafb;border-left:4px solid #0f766e;padding:12px 16px;border-radius:0 8px 8px 0;">
          <p style="margin:0;font-size:13px;color:#374151;">
            The full matrix report is attached as a PDF. This is a record of what the shop counted —
            <strong>no stock was added or removed</strong>.
          </p>
        </div>
      </div>
    </div>`
}

export interface SendShopStockCountReportArgs {
	adminId: string
	to: string
	reportHtml: string
	summary: CountSummaryForEmail
}

export async function sendShopStockCountReport({
	adminId,
	to,
	reportHtml,
	summary,
}: SendShopStockCountReportArgs): Promise<void> {
	const pdfBase64 = await htmlToPdfBase64(reportHtml)

	await sendEdgeEmail({
		to,
		adminId,
		subject: `Shop stock count ${summary.countNumber}`,
		html: buildEmailBody(
			summary,
			`Here is your copy of shop stock count <strong>${summary.countNumber}</strong>${
				summary.locationName ? ` at ${summary.locationName}` : ''
			}.`
		),
		attachments: [
			{
				filename: `shop-stock-count-${summary.countNumber}.pdf`,
				content: pdfBase64,
				contentType: 'application/pdf',
				encoding: 'base64',
			},
		],
	})
}

export async function recordShopStockCountDelivery({
	adminId,
	countId,
	to,
	error,
	printed,
}: {
	adminId: string
	countId: string
	to?: string | null
	error?: string | null
	printed?: boolean
}): Promise<void> {
	try {
		await supabase.rpc('record_shop_stock_count_delivery', {
			p_admin_id: adminId,
			p_count_id: countId,
			p_to: to ?? null,
			p_error: error ?? null,
			p_printed: Boolean(printed),
		})
	} catch (err) {
		log.warn('Could not record report delivery', {
			countId,
			error: err instanceof Error ? err.message : String(err),
		})
	}
}
